/**
 * pasteTable.ts - Pure mutation helpers for the table paste pipeline.
 *
 * These functions isolate the data transformation portions of the paste flow so
 * that they can be unit tested without rendering `TableEditable`. Orchestration
 * concerns that require component / hook state - clipboard parsing, file
 * attachment detection, selection resolution, paste validation, async selector
 * resolution, capacity checks, analytics, and persistence - remain in
 * `useTableMutations` and the component.
 *
 * The pipeline splits into three stages, matching the original ordering in
 * `TableEditable._pasteTable`:
 *   1. `preparePastedCells` - transpose, column truncation, merged-cell shaping,
 *      and tiling so the pasted region at least fills the selection.
 *   2. `processPastedTypedValues` - normalize typed values based on whether the
 *      destination table supports them.
 *   3. `pasteTable` - write the prepared cells into the table via
 *      `excess.ops.paste`.
 */

import _ from 'underscore';

import isMergedCell from 'notebook/structured-tables/util/isMergedCell';
import {type PastedCell} from 'notebook/tables/constants/CellConstants';
import {type Table} from 'notebook/tables/constants/TableConstants';
import * as TableLayoutConstants from 'notebook/tables/constants/TableLayoutConstants';
import {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import excess from 'notebook/tables/excess';
import {type EvaluatedTable} from 'notebook/tables/excess/EvaluationConstants';
import {type TableOpDiff} from 'notebook/tables/excess/ops/TableOpConstants';
import {extractSampleFromStageRunSample} from 'notebook/tables/typed-values/StageRunSampleUtil';
import transpose from 'notebook/tables/util/transpose';

const {TABLE_MAX_COLUMNS} = TableLayoutConstants;

type PastedCells = Array<Array<PastedCell>>;

/** Options controlling how pasted cells are shaped to fit the destination selection. */
export interface PreparePastedCellsOptions {
  /** Whether to transpose the pasted data before tiling (context-menu "Paste transpose"). */
  transpose?: boolean;
  /** Whether merged cells in the paste source should be preserved. */
  preserveMergedCellsInPaste: boolean;
  /** Whether the destination table forbids adding custom columns (caps paste width). */
  disallowCustomColumns: boolean;
}

/** The result of writing prepared cells into a table. */
export interface PasteTableResult {
  /** The new table after the paste. */
  table: Table;
  /** The region that was pasted into. */
  box: MultiSelectionBox;
  /** Operation descriptor for undo/redo tracking. */
  tableOp: TableOpDiff;
}

/**
 * Whether the destination table supports typed values, i.e. any column has a
 * column definition constraint. Mirrors `TableEditable._areTypedValuesAllowed`.
 */
export function computeAreTypedValuesAllowed(table: Table): boolean {
  return table.columnConstraints.some((columnConstraint) =>
    Boolean(columnConstraint && columnConstraint.columnDefinition)
  );
}

function fillToDimension<T>(dim: number, array: Array<T>): Array<T> {
  // Output should have array at least once, even if it is more than dim.
  const out = array.slice();
  // Fill it as long as it is less than the dimension
  while (out.length + array.length <= dim) {
    out.push(...Array.from(array || []));
  }
  return out;
}

function expandMergedCellWithHiddenCells(
  cell: PastedCell,
  mergeRowSpan?: number,
  mergeColSpan?: number
): PastedCells {
  const result: PastedCells = [];
  for (let i = 0; i < (mergeRowSpan || 1); i++) {
    const row: Array<PastedCell> = [];
    for (let j = 0; j < (mergeColSpan || 1); j++) {
      if (i === 0 && j === 0) {
        row.push(cell);
      } else {
        row.push({text: ''});
      }
    }
    result.push(row);
  }
  return result;
}

/**
 * Shape the raw pasted `cells` so that they tile the destination selection `box`.
 *
 * Handles (in order): transpose, truncation to the table's max column count,
 * single/merged cell reshaping when pasting a single or fully-merged source into
 * a single or fully-merged destination, and tiling to fill the selection height
 * and width. Pure — extracted verbatim from `TableEditable._pasteTable`.
 *
 * @param table - The destination table.
 * @param computedTable - The evaluated destination table (for merged-box lookup).
 * @param cells - The raw pasted cells.
 * @param box - The merge-expanded destination selection box.
 * @param options - Transpose / merge-preservation / column constraints.
 * @returns The tiled cells ready for validation and pasting.
 */
export function preparePastedCells(
  table: Table,
  computedTable: EvaluatedTable,
  cells: PastedCells,
  box: MultiSelectionBox,
  options: PreparePastedCellsOptions
): PastedCells {
  let result = options.transpose ? transpose(cells) : cells;

  const {minI, maxI, minJ, maxJ} = box;
  const selH = maxI - minI + 1;
  const selW = maxJ - minJ + 1;

  const maxColumns = options.disallowCustomColumns ? table.table[0].length : TABLE_MAX_COLUMNS;

  // But first, truncate the data to paste if it results in a table wider than MAX_COLUMN
  const extraColumns = result[0].length + minJ - maxColumns;
  if (extraColumns > 0) {
    result = result.map((row) => row.slice(0, -extraColumns));
  }

  const isSingleOrMergedCell =
    (result.length === 1 && result[0].length === 1) ||
    ((result[0][0].mergeRowSpan ?? 1) === result.length &&
      (result[0][0].mergeColSpan ?? 1) === result[0].length);

  const destinationComputedCell = computedTable[minI][minJ];
  const destinationCell = table.table[minI][minJ];
  const isDestinationSingleOrMergedCell =
    (selH === 1 && selW === 1) ||
    Boolean(
      destinationComputedCell.mergedBox &&
      destinationComputedCell.mergedBox.minI === minI &&
      destinationComputedCell.mergedBox.maxI === maxI &&
      destinationComputedCell.mergedBox.minJ === minJ &&
      destinationComputedCell.mergedBox.maxJ === maxJ
    );

  if (isSingleOrMergedCell && isDestinationSingleOrMergedCell) {
    const sourceCell = result[0][0];
    // When pasting a merged cell into a single cell, just paste the value, not the merged cell
    if (options.preserveMergedCellsInPaste && isMergedCell(sourceCell)) {
      const {mergeColSpan, mergeRowSpan, ...pastedCell} = sourceCell;
      result = [[pastedCell]];
    }
    // When pasting a single cell into a merged cell, paste the value and keep the original merge.
    if (destinationCell && isMergedCell(destinationCell)) {
      const pastedCell: PastedCell = {...sourceCell};
      if (destinationCell.mergeColSpan) {
        pastedCell.mergeColSpan = destinationCell.mergeColSpan;
      }
      if (destinationCell.mergeRowSpan) {
        pastedCell.mergeRowSpan = destinationCell.mergeRowSpan;
      }
      result = expandMergedCellWithHiddenCells(pastedCell, pastedCell.mergeRowSpan, pastedCell.mergeColSpan);
    }
  }

  // Tile the (possibly reshaped) cells so they at least fill the selection.
  return fillToDimension(
    selH,
    result.map((row) => fillToDimension(selW, row))
  );
}

/**
 * Normalize typed values on the prepared cells based on whether the destination
 * table supports typed values. Mirrors the typed-value handling in
 * `TableEditable._pasteTable`.
 *
 * When typed values are allowed, cells carrying a typed value have their text
 * blanked (the typed value is authoritative). When not allowed, the `typedValue`
 * property is stripped entirely. Finally, any remaining typed values are reduced
 * to their underlying sample via `extractSampleFromStageRunSample`.
 *
 * @param table - The destination table (determines whether typed values are allowed).
 * @param cells - The prepared pasted cells.
 * @returns The cells with typed values normalized.
 */
export function processPastedTypedValues(table: Table, cells: PastedCells): PastedCells {
  const areTypedValuesAllowed = computeAreTypedValuesAllowed(table);

  let result: PastedCells;
  if (areTypedValuesAllowed) {
    // Empty out text if typedValue is specified.
    result = cells.map((row) => row.map((cell) => (cell.typedValue ? {...cell, text: ''} : cell)));
  } else {
    // eslint-disable-next-line no-restricted-syntax
    result = cells.map((row) => row.map((cell) => _.omit(cell, 'typedValue') as PastedCell));
  }

  return result.map((row) =>
    row.map((cell) =>
      cell.typedValue ? {...cell, typedValue: extractSampleFromStageRunSample(cell.typedValue)} : cell
    )
  );
}

/**
 * Write the prepared `cells` into `table` at (`minI`, `minJ`) via `excess.ops.paste`,
 * extending the table as needed. Pure wrapper preserving the original call in
 * `TableEditable._pasteTable`.
 *
 * @param table - The destination table.
 * @param computedTable - The evaluated destination table.
 * @param cells - The prepared, typed-value-normalized cells to paste.
 * @param minI - The top row at which to paste.
 * @param minJ - The left column at which to paste.
 * @param preserveMergedCellsInPaste - Whether to preserve merged cells while pasting.
 * @returns The new table, the region pasted into, and the table op.
 */
export function pasteTable(
  table: Table,
  computedTable: EvaluatedTable,
  cells: PastedCells,
  minI: number,
  minJ: number,
  preserveMergedCellsInPaste: boolean
): PasteTableResult {
  const {
    table: newTable,
    box,
    tableOp,
  } = excess.ops.paste(table, computedTable, cells, minI, minJ, preserveMergedCellsInPaste);
  return {table: newTable, box, tableOp};
}
